import { faClockRotateLeft, faBomb } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState } from "react"
import { LoaderFunctionArgs, useLoaderData } from "react-router-dom"
import { format } from "date-fns"
import { utcToZonedTime } from "date-fns-tz"

import { fetchAPI } from "../utils"
import { Header } from "../components"

interface WorkflowStepDetail {
  step: string
  status: string
  serviceName?: string
  pod?: string
  createdAt: string
  completedAt: string
  errorMessage?: string
  stackTrace?: string
}

interface WorkflowHistory {
  workflowId: string
  steps: WorkflowStepDetail[]
  state: Record<string, unknown>
}

interface LoaderResponse {
  history: WorkflowHistory | null
  workflowId: string
  error?: string
}

async function loader({ request }: LoaderFunctionArgs): Promise<LoaderResponse> {
  const url = new URL(request.url)
  const workflowId = url.searchParams.get("workflowId") ?? ""

  if (workflowId === "") return { history: null, workflowId }

  const data = await fetchAPI<WorkflowHistory>(`/api/queues/${workflowId}/history`)

  return { history: data.result ?? null, workflowId }
}

function LocalTime({ time }: Readonly<{ time: string }>) {
  try {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const localDate = utcToZonedTime(new Date(time), localTimeZone)
    return <span>{format(localDate, "yyyy-MM-dd HH:mm:ss")}</span>
  } catch {
    return <span>{time}</span>
  }
}

function statusBadgeClass(status: string) {
  switch (status?.toLowerCase()) {
    case "complete":
    case "completed":
      return "badge badge-success badge-sm"
    case "error":
    case "failed":
      return "badge badge-error badge-sm"
    case "inprogress":
    case "processing":
      return "badge badge-warning badge-sm"
    default:
      return "badge badge-ghost badge-sm"
  }
}

function StepRow({ step, index }: Readonly<{ step: WorkflowStepDetail; index: number }>) {
  const [showTrace, setShowTrace] = useState(false)

  return (
    <>
      <tr className="hover">
        <td className="text-base-content/60 text-xs tabular-nums">{index + 1}</td>
        <td className="font-medium text-sm">{step.step}</td>
        <td>
          <span className={statusBadgeClass(step.status)}>{step.status}</span>
        </td>
        <td className="text-sm">{step.serviceName ?? "—"}</td>
        <td className="text-sm">{step.pod ?? "—"}</td>
        <td className="text-xs whitespace-nowrap">
          <LocalTime time={step.createdAt} />
        </td>
        <td className="text-xs whitespace-nowrap">
          <LocalTime time={step.completedAt} />
        </td>
        <td>
          {(step.errorMessage?.trim() || step.stackTrace?.trim()) && (
            <button
              type="button"
              title="Show error / trace"
              onClick={() => setShowTrace((showTrace) => !showTrace)}
              className="btn btn-ghost btn-xs gap-1"
            >
              <FontAwesomeIcon icon={faBomb} />
              {showTrace ? "Hide" : "Trace"}
            </button>
          )}
        </td>
      </tr>
      {showTrace && (step.errorMessage?.trim() || step.stackTrace?.trim()) && (
        <tr>
          <td colSpan={8} className="bg-base-200 p-0">
            {step.errorMessage?.trim() ? (
              <h6 className="m-3 mb-0 text-sm font-semibold text-base-content">{step.errorMessage.trim()}</h6>
            ) : null}
            {step.stackTrace?.trim() ? (
              <code className="block max-h-48 overflow-auto p-3 text-xs">
                <pre className="whitespace-pre-wrap break-words">{step.stackTrace?.trim()}</pre>
              </code>
            ) : null}
          </td>
        </tr>
      )}
    </>
  )
}

const Route = () => {
  const { history, workflowId } = useLoaderData() as LoaderResponse

  return (
    <div className="flex h-screen w-full flex-col items-start justify-start gap-5 overflow-y-auto overflow-x-hidden bg-base-300 px-5 py-5 scrollbar-none">
      <div className="w-full space-y-5">
        <Header />
        <div className="flex flex-col items-start gap-3">
          <button type="button" onClick={() => globalThis.history.back()} className="btn btn-outline btn-accent btn-xs">
            ← Back
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <FontAwesomeIcon icon={faClockRotateLeft} className="text-base-content" />
            <h1 className="text-sm font-bold text-base-content">Workflow History</h1>
            {workflowId !== "" && (
              <span className="font-mono text-xs text-base-content/60 break-all">{workflowId}</span>
            )}
          </div>
        </div>
      </div>
      <div className="divider" />

      {workflowId === "" && <p className="text-sm text-base-content/60">No workflow ID provided.</p>}

      {workflowId !== "" && history === null && (
        <p className="text-sm text-base-content/60">No history found for this workflow.</p>
      )}

      {history != null && (
        <div className="flex w-full flex-col gap-6">
          {/* Steps timeline */}
          <section className="w-full">
            <h2 className="mb-3 font-semibold text-base-content text-sm">
              Steps{" "}
              <span className="font-normal text-base-content/60">({history.steps.length})</span>
            </h2>
            {history.steps.length === 0 ? (
              <p className="text-sm text-base-content/60">No steps recorded.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-accent bg-base-100">
                <table className="table table-zebra table-sm w-full">
                  <thead>
                    <tr className="text-xs text-base-content/60">
                      <th>#</th>
                      <th>Step</th>
                      <th>Status</th>
                      <th>Service</th>
                      <th>Pod</th>
                      <th>Created</th>
                      <th>Completed</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.steps.map((step, i) => (
                      <StepRow key={`${step.step}-${i}`} step={step} index={i} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* State (payload) */}
          {Object.keys(history.state).length > 0 && (
            <section className="w-full">
              <h2 className="mb-3 font-semibold text-base-content text-sm">State</h2>
              <div className="rounded-lg border border-accent bg-base-100 p-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                  {Object.entries(history.state).map(([key, value]) => (
                    <p key={key} className="flex min-w-0 gap-2 text-base-content">
                      <span className="text-base-content/60 shrink-0">{key}:</span>
                      <span className="truncate font-bold">
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value as string | number | boolean | bigint | null | undefined)}
                      </span>
                    </p>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

Route.loader = loader

export default Route
